/**
 * Responsible making sure a source element value matches to a target element
 * value.
 * @module elements/fields/validate-same-as
 */
import $ from 'jquery';
import { DEFAULT_MESSAGE } from './validator-constants';

export { DEFAULT_MESSAGE };

export const NS = 'validate-same-as';

/**
 * Responsible for making the source element either valid or invalid.
 * @param {jQuery} $source - Element with reference to target
 * @param {string} message - The error message
 */
const verifyError = ($source, message = '') => {
  const source = $source[0];
  source.setCustomValidity(message);
  source.checkValidity();

  if (!message) {
    const evt = new CustomEvent('valid');
    source.dispatchEvent(evt);
  }
};

/**
 * Responsible for validating the source and target element values and determine
 * if an error has been found.
 * @param {jQuery} $source - Element with reference to target
 * @param {jQuery} $target - Target element
 * @returns {string} The error message
 */
const validateMatching = ($source, $target) => {
  const sourceValue = $source.val();
  const targetValue = $target.val();
  if (sourceValue !== targetValue) {
    return $source.data('errorMessage') || DEFAULT_MESSAGE;
  }

  return '';
};

/**
 * Responsible for matching the source value with the target value and makes
 * target element either valid or invalid.
 * @param {jQuery} $source - Element with reference to target
 * @param {jQuery} $target - Target element
 */
const matchesTarget = ($source, $target) => {
  const message = validateMatching($source, $target);
  verifyError($source, message);
};

/**
 * Responsible for finding the target element.
 * @param {jQuery} $source - Element with reference to target
 * @returns {jQuery} The target element
 */
const getTarget = ($source) => {
  const selector = $source.data('validateSameAs');
  const $form = $($source.prop('form'));
  const $target = $form.find(selector);
  if (!$target.length) {
    throw new Error(`No matching selector ${selector} found for same as`);
  }

  return $target;
};

/**
 * Used for hooking into a validator data attribute.
 * @param {jQuery} $source - Element with reference to target
 * @returns {string} Error message if an error is found
 */
export const validateSameAs = ($source) => {
  const $target = getTarget($source);

  $target.on(`blur.${NS}`, () => matchesTarget($source, $target));

  return validateMatching($source, $target);
};

/**
 * Responsible for finding a target element and ensuring that both fields have
 * the same values.
 * @param {HTMLElement} source - Element with reference to target
 * @returns {Object} Additional methods attached validate same as
 */
export default (source) => {
  const $source = $(source);
  const $target = getTarget($source);

  validateSameAs($source);

  $source
    .on(`blur.${NS}`, () => {
      const message = validateMatching($source, $target);
      verifyError($source, message);
    });

  return {
    teardown: () => {
      $source.off(`.${NS}`);
      $target.off(`.${NS}`);
    },
  };
};


// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/validate-same-as.js